import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  Plus,
  X,
  ListChecks,
  CheckCircle2,
  ArrowRightCircle,
  Banknote,
  Receipt,
  Undo2,
} from "lucide-react";
import StatCard from "../components/agentProgram/StatCard";
import StatusPill from "../components/agentProgram/StatusPill";
import SectionCard from "../components/agentProgram/SectionCard";
import EmptyState from "../components/agentProgram/EmptyState";

const API = process.env.REACT_APP_API_URL;

const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const dateText = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "-";

const stageLabel = {
  lead_submitted: "Lead Submitted",
  marked_genuine: "Marked Genuine",
  application_accepted: "Application Accepted",
  disbursed: "Disbursed",
  clawed_back: "Clawed Back",
};

const stageTone = {
  lead_submitted: "info",
  marked_genuine: "info",
  application_accepted: "warning",
  disbursed: "success",
  clawed_back: "danger",
};

// Tailwind's JIT scanner needs literal class strings, not interpolated
// ones — so this maps to full static class names rather than building them.
const stageCardTone = {
  lead_submitted: "bg-blue-50 text-blue-600",
  marked_genuine: "bg-blue-50 text-blue-600",
  application_accepted: "bg-amber-50 text-amber-600",
  disbursed: "bg-emerald-50 text-emerald-600",
  clawed_back: "bg-red-50 text-red-600",
};

const funnelStages = ["lead_submitted", "marked_genuine", "application_accepted", "disbursed"];

const emptyLeadForm = { customerName: "", customerPhone: "", customerPan: "", loanAmountRequested: "" };

export default function AgentProgramLeads() {
  const { userInfo } = useSelector((state) => state.userLogin || {});
  const { getRoleData } = useSelector((state) => state.getRoleInfo || {});
  const role = getRoleData || userInfo?.role || {};
  const isMerchantUser = role?.name?.toLowerCase() === "merchant" || !!userInfo?.merchantId;

  const [leads, setLeads] = useState([]);
  const [stageFilter, setStageFilter] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [submitOpen, setSubmitOpen] = useState(false);
  const [leadForm, setLeadForm] = useState(emptyLeadForm);

  const [genuineModal, setGenuineModal] = useState({ open: false, row: null, customerPan: "" });
  const [acceptModal, setAcceptModal] = useState({ open: false, row: null, lenderName: "" });
  const [disburseModal, setDisburseModal] = useState({ open: false, row: null, disbursedLoanAmount: "", commissionAmount: "" });
  const [commissionModal, setCommissionModal] = useState({ open: false, row: null, commissionReceivedAmount: "" });
  const [clawbackModal, setClawbackModal] = useState({ open: false, row: null, reversedAmount: "", reason: "" });

  const authConfig = {
    headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {},
  };

  const fetchLeads = async () => {
    setError("");
    try {
      const { data } = await axios.get(`${API}/agent-program/leads`, {
        ...authConfig,
        params: stageFilter ? { stage: stageFilter } : {},
      });
      setLeads(data || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load leads");
    }
  };

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageFilter]);

  const runAction = async (fn, successMessage) => {
    setMessage("");
    setError("");
    try {
      await fn();
      setMessage(successMessage);
      await fetchLeads();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Action failed");
    }
  };

  const submitLead = async () => {
    await runAction(async () => {
      await axios.post(
        `${API}/agent-program/leads`,
        {
          ...leadForm,
          loanAmountRequested: Number(leadForm.loanAmountRequested),
          agentId: userInfo?.merchantId,
        },
        authConfig
      );
      setSubmitOpen(false);
      setLeadForm(emptyLeadForm);
    }, "Lead submitted.");
  };

  // Leads auto-created from the public enquiry form don't have a PAN yet
  // (only a PAN card image) — prompt for it here; leads submitted directly
  // through the Agent Program form already have one, so this goes straight
  // through.
  const markGenuine = (row) => {
    if (row.customerPan) {
      return runAction(
        () => axios.put(`${API}/agent-program/leads/${row._id}/mark-genuine`, {}, authConfig),
        "Lead marked genuine."
      );
    }
    setGenuineModal({ open: true, row, customerPan: "" });
  };

  const submitGenuine = () =>
    runAction(async () => {
      await axios.put(
        `${API}/agent-program/leads/${genuineModal.row._id}/mark-genuine`,
        { customerPan: genuineModal.customerPan },
        authConfig
      );
      setGenuineModal({ open: false, row: null, customerPan: "" });
    }, "Lead marked genuine.");

  const submitAccept = () =>
    runAction(async () => {
      await axios.put(
        `${API}/agent-program/leads/${acceptModal.row._id}/accept`,
        { lenderName: acceptModal.lenderName },
        authConfig
      );
      setAcceptModal({ open: false, row: null, lenderName: "" });
    }, "Application accepted.");

  const submitDisburse = () =>
    runAction(async () => {
      await axios.put(
        `${API}/agent-program/leads/${disburseModal.row._id}/disburse`,
        {
          disbursedLoanAmount: Number(disburseModal.disbursedLoanAmount),
          commissionAmount: disburseModal.commissionAmount ? Number(disburseModal.commissionAmount) : undefined,
        },
        authConfig
      );
      setDisburseModal({ open: false, row: null, disbursedLoanAmount: "", commissionAmount: "" });
    }, "Loan marked disbursed.");

  const submitCommission = () =>
    runAction(async () => {
      await axios.put(
        `${API}/agent-program/leads/${commissionModal.row._id}/commission-received`,
        { commissionReceivedAmount: Number(commissionModal.commissionReceivedAmount) },
        authConfig
      );
      setCommissionModal({ open: false, row: null, commissionReceivedAmount: "" });
    }, "Commission recorded.");

  const submitClawback = () =>
    runAction(async () => {
      await axios.put(
        `${API}/agent-program/leads/${clawbackModal.row._id}/clawback`,
        { reversedAmount: Number(clawbackModal.reversedAmount), reason: clawbackModal.reason },
        authConfig
      );
      setClawbackModal({ open: false, row: null, reversedAmount: "", reason: "" });
    }, "Commission clawed back.");

  const stageCounts = leads.reduce((acc, l) => {
    acc[l.stage] = (acc[l.stage] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="-mx-3 px-2 py-3 sm:mx-0 sm:p-5 bg-gray-50 min-h-screen w-[calc(100%+1.5rem)] sm:w-full">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-11 w-11 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
          <ListChecks size={21} className="text-indigo-600" />
        </div>
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Agent Program Leads</h1>
          <p className="text-sm text-gray-500 mt-0.5">Stage progression, points and payouts</p>
        </div>
      </div>

      {!isMerchantUser && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {funnelStages.map((stage) => (
            <StatCard
              key={stage}
              title={stageLabel[stage]}
              value={stageCounts[stage] || 0}
              icon={ListChecks}
              tone={stageCardTone[stage]}
            />
          ))}
        </div>
      )}

      {message && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-4 py-3 text-sm mb-4">
          {message}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{error}</div>
      )}

      <SectionCard
        icon={ListChecks}
        title="Leads"
        subtitle={`${leads.length} shown`}
        bodyClassName=""
        headerRight={
          <div className="flex gap-2 items-center">
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              className="border border-gray-200 rounded-lg text-sm px-3 py-2 bg-white"
            >
              <option value="">All stages</option>
              {Object.entries(stageLabel).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            {!!role?.agentProgramLeadSubmit && (
              <button
                onClick={() => setSubmitOpen(true)}
                className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium px-4 py-2 rounded-lg"
              >
                <Plus size={16} /> Submit Lead
              </button>
            )}
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                {(isMerchantUser
                  ? ["Customer", "Phone", "Loan Amount", "Stage", "Submitted", "Actions"]
                  : ["Customer", "Agent", "Phone", "Loan Amount", "Stage", "Submitted", "Actions"]
                ).map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={isMerchantUser ? 6 : 7}>
                    <EmptyState icon={ListChecks} title="No leads found" compact />
                  </td>
                </tr>
              ) : (
                leads.map((row) => (
                  <tr key={row._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap">
                      {row.customerName}
                      {row.loanEnquiry && (
                        <span className="block text-[11px] text-gray-400">via enquiry form</span>
                      )}
                      {!row.customerPan && !row.duplicateOf && (
                        <span className="block text-[11px] text-amber-600">PAN pending</span>
                      )}
                      {row.duplicateOf && (
                        <span className="block text-[11px] text-orange-500">duplicate — earns nothing</span>
                      )}
                    </td>
                    {!isMerchantUser && (
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {row.agent?.name || "—"}
                        {row.agent?.identifier && (
                          <span className="block text-[11px] text-gray-400">{row.agent.identifier}</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{row.customerPhone}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">₹{money(row.loanAmountRequested)}</td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <StatusPill label={stageLabel[row.stage] || row.stage} tone={stageTone[row.stage] || "neutral"} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{dateText(row.createdAt)}</td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <div className="flex gap-2 flex-wrap">
                        {row.stage === "lead_submitted" && !!role?.agentProgramMarkGenuine && !isMerchantUser && (
                          <button onClick={() => markGenuine(row)} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100">
                            <CheckCircle2 size={13} /> Mark Genuine
                          </button>
                        )}
                        {row.stage === "marked_genuine" && !!role?.agentProgramMarkAccepted && !isMerchantUser && (
                          <button
                            onClick={() => setAcceptModal({ open: true, row, lenderName: "" })}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100"
                          >
                            <ArrowRightCircle size={13} /> Accept
                          </button>
                        )}
                        {row.stage === "application_accepted" && !!role?.agentProgramMarkDisbursed && !isMerchantUser && (
                          <button
                            onClick={() => setDisburseModal({ open: true, row, disbursedLoanAmount: row.loanAmountRequested, commissionAmount: "" })}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                          >
                            <Banknote size={13} /> Disburse
                          </button>
                        )}
                        {row.stage === "disbursed" && !!role?.agentProgramMarkCommissionReceived && !isMerchantUser && (
                          <button
                            onClick={() => setCommissionModal({ open: true, row, commissionReceivedAmount: "" })}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100"
                          >
                            <Receipt size={13} /> Commission
                          </button>
                        )}
                        {row.stage === "disbursed" && !!role?.agentProgramClawback && !isMerchantUser && (
                          <button
                            onClick={() => setClawbackModal({ open: true, row, reversedAmount: "", reason: "" })}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"
                          >
                            <Undo2 size={13} /> Clawback
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {genuineModal.open && (
        <Modal title="Mark Genuine" onClose={() => setGenuineModal({ open: false, row: null, customerPan: "" })}>
          <p className="text-xs text-gray-500 mb-3">
            This lead came from the enquiry form without a PAN on file. Enter it now — the customer's
            phone + PAN is checked here for duplicates before the lead can progress.
          </p>
          <Field label="Customer PAN">
            <input
              className="input uppercase"
              value={genuineModal.customerPan}
              onChange={(e) => setGenuineModal({ ...genuineModal, customerPan: e.target.value.toUpperCase() })}
            />
          </Field>
          <ModalActions onCancel={() => setGenuineModal({ open: false, row: null, customerPan: "" })} onConfirm={submitGenuine} confirmLabel="Mark Genuine" />
        </Modal>
      )}

      {submitOpen && (
        <Modal title="Submit Agent Program Lead" onClose={() => setSubmitOpen(false)}>
          <div className="grid grid-cols-1 gap-3">
            <Field label="Customer Name">
              <input className="input" value={leadForm.customerName} onChange={(e) => setLeadForm({ ...leadForm, customerName: e.target.value })} />
            </Field>
            <Field label="Phone">
              <input className="input" value={leadForm.customerPhone} onChange={(e) => setLeadForm({ ...leadForm, customerPhone: e.target.value })} />
            </Field>
            <Field label="PAN">
              <input className="input uppercase" value={leadForm.customerPan} onChange={(e) => setLeadForm({ ...leadForm, customerPan: e.target.value.toUpperCase() })} />
            </Field>
            <Field label="Loan Amount Requested">
              <input type="number" className="input" value={leadForm.loanAmountRequested} onChange={(e) => setLeadForm({ ...leadForm, loanAmountRequested: e.target.value })} />
            </Field>
          </div>
          <ModalActions onCancel={() => setSubmitOpen(false)} onConfirm={submitLead} confirmLabel="Submit" />
        </Modal>
      )}

      {acceptModal.open && (
        <Modal title="Accept Application" onClose={() => setAcceptModal({ open: false, row: null, lenderName: "" })}>
          <Field label="Lender Name">
            <input className="input" value={acceptModal.lenderName} onChange={(e) => setAcceptModal({ ...acceptModal, lenderName: e.target.value })} />
          </Field>
          <ModalActions onCancel={() => setAcceptModal({ open: false, row: null, lenderName: "" })} onConfirm={submitAccept} confirmLabel="Accept" />
        </Modal>
      )}

      {disburseModal.open && (
        <Modal title="Mark Disbursed" onClose={() => setDisburseModal({ open: false, row: null, disbursedLoanAmount: "", commissionAmount: "" })}>
          <div className="grid grid-cols-1 gap-3">
            <Field label="Disbursed Loan Amount">
              <input
                type="number"
                className="input"
                value={disburseModal.disbursedLoanAmount}
                onChange={(e) => setDisburseModal({ ...disburseModal, disbursedLoanAmount: e.target.value })}
              />
            </Field>
            <Field label="Commission Amount (only if payout trigger is 'disbursal')">
              <input
                type="number"
                className="input"
                value={disburseModal.commissionAmount}
                onChange={(e) => setDisburseModal({ ...disburseModal, commissionAmount: e.target.value })}
              />
            </Field>
          </div>
          <ModalActions onCancel={() => setDisburseModal({ open: false, row: null, disbursedLoanAmount: "", commissionAmount: "" })} onConfirm={submitDisburse} confirmLabel="Disburse" />
        </Modal>
      )}

      {commissionModal.open && (
        <Modal title="Record Commission Received" onClose={() => setCommissionModal({ open: false, row: null, commissionReceivedAmount: "" })}>
          <Field label="Commission Received Amount">
            <input
              type="number"
              className="input"
              value={commissionModal.commissionReceivedAmount}
              onChange={(e) => setCommissionModal({ ...commissionModal, commissionReceivedAmount: e.target.value })}
            />
          </Field>
          <ModalActions onCancel={() => setCommissionModal({ open: false, row: null, commissionReceivedAmount: "" })} onConfirm={submitCommission} confirmLabel="Save" />
        </Modal>
      )}

      {clawbackModal.open && (
        <Modal title="Claw Back Commission" onClose={() => setClawbackModal({ open: false, row: null, reversedAmount: "", reason: "" })}>
          <div className="grid grid-cols-1 gap-3">
            <Field label="Reversed Amount">
              <input
                type="number"
                className="input"
                value={clawbackModal.reversedAmount}
                onChange={(e) => setClawbackModal({ ...clawbackModal, reversedAmount: e.target.value })}
              />
            </Field>
            <Field label="Reason">
              <input className="input" value={clawbackModal.reason} onChange={(e) => setClawbackModal({ ...clawbackModal, reason: e.target.value })} />
            </Field>
          </div>
          <ModalActions onCancel={() => setClawbackModal({ open: false, row: null, reversedAmount: "", reason: "" })} onConfirm={submitClawback} confirmLabel="Claw Back" danger />
        </Modal>
      )}

      <style>{`.input { border: 1px solid #e5e7eb; border-radius: 0.5rem; padding: 0.5rem 0.75rem; font-size: 0.875rem; width: 100%; }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md p-5 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalActions({ onCancel, onConfirm, confirmLabel, danger }) {
  return (
    <div className="flex justify-end gap-2 mt-5">
      <button onClick={onCancel} className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
        Cancel
      </button>
      <button
        onClick={onConfirm}
        className={`text-sm px-4 py-2 rounded-lg text-white ${danger ? "bg-red-600 hover:bg-red-700" : "bg-gray-900 hover:bg-gray-800"}`}
      >
        {confirmLabel}
      </button>
    </div>
  );
}
